import { COLS, type Row } from './types.ts';

/**
 * Authoring markup (brief §3.4): one token per cell, exactly as on air.
 *
 *   [K] [R] [G] [Y] [B] [M] [C] [W]      alpha colours
 *   [gK] [gR] [gG] [gY] [gB] [gM] [gC] [gW]  mosaic colours
 *   [F] flash  [ST] steady  [D] double height  [N] normal height
 *   [H] conceal  [S] separated  [T] contiguous
 *   [NB] new background  [BB] black background
 *   [HG] hold mosaics  [RG] release mosaics
 *   [SB] start box  [EB] end box  [ESC] switch G0
 *   [#7f] any code as two hex digits (for raw sixels in plain-text files)
 *   [[  a literal '['
 *
 * Any other character is stored as its 7-bit code, so what you type is the
 * teletext code: '#' is £ on screen, '_' is #, '`' is —. Rows are padded
 * or truncated to 40 cells.
 */
export const TOKENS: Record<string, number> = {
  K: 0x00, R: 0x01, G: 0x02, Y: 0x03, B: 0x04, M: 0x05, C: 0x06, W: 0x07,
  F: 0x08, ST: 0x09, EB: 0x0a, SB: 0x0b, N: 0x0c, D: 0x0d,
  gK: 0x10, gR: 0x11, gG: 0x12, gY: 0x13, gB: 0x14, gM: 0x15, gC: 0x16, gW: 0x17,
  H: 0x18, T: 0x19, S: 0x1a, ESC: 0x1b, BB: 0x1c, NB: 0x1d, HG: 0x1e, RG: 0x1f,
};

const CODE_TO_TOKEN = new Map<number, string>();
for (const [t, c] of Object.entries(TOKENS)) CODE_TO_TOKEN.set(c, t);

/** Number of cells an authored row would occupy before padding/truncation. */
export function rowLength(src: string): number {
  return tokenize(src, Infinity).length;
}

export function compileRow(src: string): Row {
  const out = tokenize(src, COLS);
  while (out.length < COLS) out.push(0x20);
  return out;
}

function tokenize(src: string, max: number): Row {
  const out: Row = [];
  let i = 0;
  while (i < src.length && out.length < max) {
    const ch = src[i]!;
    if (ch === '[') {
      if (src[i + 1] === '[') {
        out.push(0x5b);
        i += 2;
        continue;
      }
      const j = src.indexOf(']', i);
      if (j > i) {
        const t = src.slice(i + 1, j);
        if (t in TOKENS) {
          out.push(TOKENS[t]!);
          i = j + 1;
          continue;
        }
        const hex = /^#([0-9a-fA-F]{2})$/.exec(t);
        if (hex) {
          out.push(parseInt(hex[1]!, 16) & 0x7f);
          i = j + 1;
          continue;
        }
      }
    }
    out.push(src.charCodeAt(i) & 0x7f);
    i++;
  }
  return out;
}

/** Compile 24 authored rows (1–24) into a 25-row grid with a blank header row. */
export function compilePage(rows: string[]): Row[] {
  const out: Row[] = [compileRow('')];
  for (let r = 0; r < 24; r++) out.push(compileRow(rows[r] ?? ''));
  return out;
}

/** Inverse of compileRow — for debugging, fixtures and round-trip tests. */
export function decompileRow(row: Row): string {
  let s = '';
  for (let x = 0; x < COLS; x++) {
    const c = (row[x] ?? 0x20) & 0x7f;
    if (c < 0x20) s += `[${CODE_TO_TOKEN.get(c)}]`;
    else if (c === 0x5b) s += '[[';
    else if (c === 0x7f) s += '[#7f]';
    else s += String.fromCharCode(c);
  }
  return s.replace(/ +$/, '');
}

/** Repeat a character n times — handy for mosaic bars in authored pages. */
export function fill(ch: string, n: number): string {
  return ch.repeat(Math.max(0, n));
}
